/** Enums e labels — classificação de prestadores/contratos (Etapa 1). */

export const TIPOS_CONTRATO = [
  { value: 'prestacao_servico', label: 'Prestação de serviço' },
  { value: 'locacao', label: 'Locação' },
  { value: 'terceirizado', label: 'Terceirizado' },
  { value: 'comodato', label: 'Comodato' },
  { value: 'compra_fornecimento', label: 'Compra / fornecimento' },
  { value: 'manutencao', label: 'Manutenção' },
  { value: 'outro', label: 'Outro' },
];

export const CATEGORIAS_SERVICO = [
  { value: 'servico_medico', label: 'Serviço médico' },
  { value: 'servico_odontologico', label: 'Serviço odontológico' },
  { value: 'grafica', label: 'Gráfica' },
  { value: 'ti', label: 'TI' },
  { value: 'manutencao_predial', label: 'Manutenção predial' },
  { value: 'limpeza_higienizacao', label: 'Limpeza / higienização' },
  { value: 'seguranca', label: 'Segurança' },
  { value: 'alimentacao_nutricao', label: 'Alimentação / nutrição' },
  { value: 'laboratorial', label: 'Laboratorial' },
  { value: 'outro', label: 'Outro' },
];

export const REGIMES_MEDICO = [
  { value: 'plantao_fixo', label: 'Plantão fixo' },
  { value: 'escala', label: 'Escala' },
  { value: 'eventual', label: 'Eventual' },
];

export const PERIODICIDADES = [
  { value: 'mensal', label: 'Mensal' },
  { value: 'bimestral', label: 'Bimestral' },
  { value: 'trimestral', label: 'Trimestral' },
  { value: 'semestral', label: 'Semestral' },
  { value: 'anual', label: 'Anual' },
  { value: 'eventual_avulso', label: 'Eventual / avulso' },
  { value: 'parcelado', label: 'Parcelado (N parcelas)' },
];

export const REGIMES_VALOR = [
  { value: 'valor_fixo', label: 'Valor fixo' },
  { value: 'valor_variavel', label: 'Valor variável' },
  { value: 'por_hora', label: 'Por hora' },
  { value: 'por_atendimento', label: 'Por atendimento' },
];

export function labelPeriodicidade(value) {
  return PERIODICIDADES.find((p) => p.value === value)?.label || value || '—';
}

export const TIPOS_MATERIAL_GRAFICA = [
  { value: 'impressos', label: 'Impressos' },
  { value: 'banners', label: 'Banners' },
  { value: 'etiquetas', label: 'Etiquetas' },
  { value: 'outros', label: 'Outros' },
];

const TIPO_LABEL = Object.fromEntries(TIPOS_CONTRATO.map((t) => [t.value, t.label]));
const CAT_LABEL = Object.fromEntries(CATEGORIAS_SERVICO.map((c) => [c.value, c.label]));

export function labelTipoContrato(value) {
  return TIPO_LABEL[value] || value || '—';
}

export function labelCategoriaServico(value) {
  return CAT_LABEL[value] || value || '—';
}

export function chipColorTipoContrato(value) {
  const map = {
    prestacao_servico: 'primary',
    locacao: 'secondary',
    terceirizado: 'warning',
    comodato: 'info',
    compra_fornecimento: 'success',
    manutencao: 'default',
    outro: 'default',
  };
  return map[value] || 'default';
}

export function chipColorCategoriaServico(value) {
  const map = {
    servico_medico: 'error',
    servico_odontologico: 'warning',
    grafica: 'info',
    ti: 'primary',
    manutencao_predial: 'default',
    limpeza_higienizacao: 'success',
    seguranca: 'secondary',
    alimentacao_nutricao: 'warning',
    laboratorial: 'info',
    outro: 'default',
  };
  return map[value] || 'default';
}

export function emptyDetalhesServico() {
  return {
    // médico
    quantidadeMedicos: '',
    especialidades: [],
    horasMensais: '',
    horasSemanais: '',
    regimeMedico: '',
    crmResponsavel: '',
    // odontológico
    quantidadeDentistas: '',
    procedimentosCobertos: '',
    croResponsavel: '',
    // gráfica
    tipoMaterial: '',
    volumeMedioMensal: '',
    // demais
    observacoes: '',
    // locação
    enderecoImovel: '',
    metragem: '',
    finalidadeEspaco: '',
    // comodato
    descricaoBem: '',
    valorReferenciaBem: '',
    // terceirizado
    postoTrabalho: '',
    quantidadeFuncionarios: '',
  };
}

export function parseDetalhesServico(raw) {
  const base = emptyDetalhesServico();
  if (!raw) return base;
  try {
    const obj = typeof raw === 'string' ? JSON.parse(raw) : raw;
    if (!obj || typeof obj !== 'object') return base;
    return { ...base, ...obj };
  } catch {
    return base;
  }
}

/** Infere tipo/categoria a partir de flags legadas (quando colunas novas estão vazias). */
export function inferClassificacaoFromLegacy(p) {
  const toB = (v) => v === 1 || v === true || v === '1' || v === 'true';
  let tipoContrato = p?.tipoContrato || '';
  let categoriaServico = p?.categoriaServico || '';
  if (!tipoContrato) {
    if (toB(p?.locacao)) tipoContrato = 'locacao';
    else if (toB(p?.terceirizada)) tipoContrato = 'terceirizado';
    else tipoContrato = 'prestacao_servico';
  }
  if (!categoriaServico) {
    if (toB(p?.servicoMedico)) categoriaServico = 'servico_medico';
    else categoriaServico = 'outro';
  }
  return { tipoContrato, categoriaServico };
}

/** Monta detalhesServico a partir de colunas legadas + JSON. */
export function mergeDetalhesFromPrestador(p) {
  const d = parseDetalhesServico(p?.detalhesServico);
  if (!d.quantidadeMedicos && p?.quantidadeMedicos) d.quantidadeMedicos = String(p.quantidadeMedicos);
  if (!d.horasMensais && p?.horasMensais) d.horasMensais = String(p.horasMensais);
  if (!d.horasSemanais && p?.horasSemanais) d.horasSemanais = String(p.horasSemanais);
  if ((!d.especialidades || !d.especialidades.length) && p?.especialidade) {
    d.especialidades = [String(p.especialidade).trim()].filter(Boolean);
  }
  if (!d.quantidadeFuncionarios && p?.quantidadeFuncionarios) {
    d.quantidadeFuncionarios = String(p.quantidadeFuncionarios);
  }
  return d;
}
